import { useCallback, useEffect, useState } from "react";
import { faPlus, faPencil, faTrash, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { isValidTrafficTotal, serverErrMsg } from "../utils/validators";
import { useThor } from "../context/ThorContext";
import BucketModal from "./BucketModal";
import ConfirmDialog from "./ConfirmDialog";

const COMPARISONS_OPTIONS = [
    { value: 'only_control', label: 'Only Control' },
    { value: 'all', label: 'All' },
];

export const BucketsTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [bucketsState, setBucketsState] = useState(buildLoadingState([]));
    const [comparisonsMode, setComparisonsMode] = useState('only_control');
    const [comparisonsUpdating, setComparisonsUpdating] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editBucket, setEditBucket] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchBuckets = useCallback(() => {
        setBucketsState(buildLoadingState([]));
        Promise.all([
            execute('GET', thor_api_urls.experimentBuckets(experimentKey)),
            execute('GET', thor_api_urls.experiment(experimentKey)).catch(() => null),
        ]).then(([data, expData]) => {
                const buckets = data?.buckets ?? data ?? [];
                setBucketsState(buildDataState(buckets));
                const mode = expData?.bucketComparisonsMode ?? expData?.experiment?.bucketComparisonsMode ?? 'only_control';
                setComparisonsMode(mode);
            })
            .catch(error => {
                console.error("Failed to fetch buckets:", error);
                setBucketsState(buildErrorState(error, []));
            });
    }, [execute, experimentKey]);

    useEffect(() => {
        fetchBuckets();
    }, [fetchBuckets]);

    const handleComparisonsChange = (newMode) => {
        if (!editMode || newMode === comparisonsMode) return;
        setComparisonsUpdating(true);
        execute('POST', thor_api_urls.bucketComparisonsMode(experimentKey, newMode))
            .then(() => {
                setComparisonsMode(newMode);
                addToast('Bucket comparisons mode updated.', 'success');
            })
            .catch(error => addToast(serverErrMsg(error, 'Failed to update comparisons mode.'), 'danger'))
            .finally(() => setComparisonsUpdating(false));
    };

    const openCreate = () => {
        setEditBucket(null);
        setModalOpen(true);
    };

    const openEdit = (bucket) => {
        setEditBucket(bucket);
        setModalOpen(true);
    };

    const handleSubmit = (form) => {
        setIsSubmitting(true);
        const request = editBucket
            ? execute('PUT', thor_api_urls.experimentBucket(experimentKey, editBucket.id), form)
            : execute('POST', thor_api_urls.experimentBuckets(experimentKey), form);
        request
            .then(() => {
                setModalOpen(false);
                fetchBuckets();
                addToast(editBucket ? 'Bucket updated.' : 'Bucket created.', 'success');
            })
            .catch(error => {
                console.error("Failed to save bucket:", error);
                addToast(serverErrMsg(error, 'Failed to save bucket.'), 'danger');
            })
            .finally(() => setIsSubmitting(false));
    };

    const handleDelete = (bucket) => {
        setDeletingId(bucket.id);
        execute('DELETE', thor_api_urls.experimentBucket(experimentKey, bucket.id))
            .then(() => {
                fetchBuckets();
                addToast('Bucket deleted.', 'success');
            })
            .catch(error => {
                console.error("Failed to delete bucket:", error);
                addToast(serverErrMsg(error, 'Failed to delete bucket.'), 'danger');
            })
            .finally(() => setDeletingId(null));
    };

    const buckets = bucketsState.data || [];
    const totalTraffic = buckets.reduce((sum, b) => sum + (b.trafficAllocation || 0), 0);
    const trafficValid = isValidTrafficTotal(totalTraffic);

    return (
        <>
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">
                        Buckets<HelpTooltip text={HELP.buckets.general} />
                    </p>
                </div>
                <div className="level-right" style={{ gap: '0.75rem', display: 'flex', alignItems: 'center' }}>
                    <span className={`has-text-weight-semibold ${trafficValid ? "has-text-success" : "has-text-danger"}`}>
                        Total: {totalTraffic}%
                        {!trafficValid && buckets.length > 0 &&
                            <span className="is-size-7 ml-2">(must be 96–101%)</span>}
                    </span>
                    <Button className="is-link is-small" onClick={openCreate} disabled={!editMode}>
                        <Icon icon={faPlus} />
                        <span>Add Bucket</span>
                    </Button>
                </div>
            </div>

            <div className="field is-horizontal mb-4" style={{ alignItems: 'center' }}>
                <div className="field-label is-small" style={{ flexGrow: 0, marginRight: '0.75rem', whiteSpace: 'nowrap' }}>
                    <label className="label is-small">
                        Bucket Comparisons<HelpTooltip text={HELP.buckets.comparisons} />
                    </label>
                </div>
                <div className="field-body">
                    <div className="field">
                        <div className="buttons has-addons">
                            {COMPARISONS_OPTIONS.map(opt => (
                                <button key={opt.value} type="button"
                                    className={`button is-small ${comparisonsMode === opt.value ? 'is-link is-selected' : ''}`}
                                    disabled={!editMode || comparisonsUpdating}
                                    onClick={() => handleComparisonsChange(opt.value)}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {buckets.length < 2 && !bucketsState.loading && !bucketsState.error && (
                <div className="notification is-warning is-light">
                    At least 2 buckets are required (including a control bucket).
                </div>
            )}

            <StateHandler showLoader={bucketsState.loading} showError={bucketsState.error}
                          loadingScreen={<SmallLoadingSpinner />}>

                <progress
                    className={`progress ${trafficValid ? "is-success" : "is-danger"}`}
                    value={Math.min(totalTraffic, 100)}
                    max="100"
                >
                    {totalTraffic}%
                </progress>

                {buckets.length === 0
                    ? (
                        <div className="has-text-centered" style={{ padding: '2rem', color: '#888' }}>
                            <p className="has-text-weight-semibold mb-2" style={{ color: '#363636' }}>No buckets yet</p>
                            <p className="is-size-7 mb-3">You need at least <strong>2 buckets</strong>: one <strong>Control</strong> (baseline) and one <strong>Treatment</strong> (the change being tested). Each bucket gets a % of eligible traffic.</p>
                            {editMode && <button type="button" className="button is-link is-small" onClick={openCreate}>Add your first bucket</button>}
                            {!editMode && <p className="is-size-7 has-text-grey">Enable Edit mode to add buckets.</p>}
                        </div>
                    )
                    : (
                        <table className="table is-fullwidth is-striped is-hoverable">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Key</th>
                                    <th>Description</th>
                                    <th className="has-text-centered">Traffic %</th>
                                    <th className="has-text-centered">Control</th>
                                    <th className="has-text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buckets.map(b => (
                                    <tr key={b.id}>
                                        <td className="has-text-grey is-size-7">{b.id}</td>
                                        <td className="has-text-weight-semibold">{b.key}</td>
                                        <td className="has-text-grey">{b.description || "—"}</td>
                                        <td className="has-text-centered">
                                            <span className="tag is-info is-light">{b.trafficAllocation}%</span>
                                        </td>
                                        <td className="has-text-centered">
                                            {b.control
                                                ? <Icon className="has-text-success" icon={faCheck} />
                                                : <Icon className="has-text-danger" icon={faXmark} />}
                                        </td>
                                        <td className="has-text-right">
                                            <div style={{ display: 'inline-flex', gap: '0.35rem' }}>
                                                <button type="button"
                                                    className="button is-small is-ghost"
                                                    aria-label="Edit bucket"
                                                    style={{ padding: '0 0.4rem' }}
                                                    disabled={!editMode || deletingId != null}
                                                    onClick={editMode ? () => openEdit(b) : undefined}>
                                                    <Icon icon={faPencil} />
                                                </button>
                                                <button type="button"
                                                    className="button is-small is-ghost has-text-danger"
                                                    aria-label={b.control ? "Cannot delete control bucket" : "Delete bucket"}
                                                    style={{ padding: '0 0.4rem' }}
                                                    disabled={!editMode || b.control || deletingId === b.id}
                                                    onClick={editMode && !b.control ? () => setConfirmDelete(b) : undefined}>
                                                    <Icon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </StateHandler>

            <BucketModal isActive={modalOpen} editBucket={editBucket} isSubmitting={isSubmitting}
                         otherBucketsTotal={buckets
                             .filter(b => !editBucket || b.id !== editBucket.id)
                             .reduce((sum, b) => sum + (b.trafficAllocation || 0), 0)}
                         onClose={() => setModalOpen(false)} onSubmit={handleSubmit} />

            <ConfirmDialog
                isActive={confirmDelete !== null}
                title="Delete Bucket"
                message={`Remove bucket "${confirmDelete?.key}"? This cannot be undone.`}
                confirmLabel="Delete"
                confirmClass="is-danger"
                onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
                onCancel={() => setConfirmDelete(null)}
            />
        </>
    );
};

export default BucketsTab;
